import React from "react";
import styles from "./BorderExamples.module.scss";

interface BorderExample {
  name: string;
  value: string;
  className: string;
  cssVariable: string;
  description: string;
}

export function BorderExamples() {
  const borderRadiusExamples: BorderExample[] = [
    {
      name: "XS",
      value: "4px",
      className: "rounded-xs",
      cssVariable: "--border-radius-xs",
      description: "Small components like tags",
    },
    {
      name: "S",
      value: "8px",
      className: "rounded-s",
      cssVariable: "--border-radius-s",
      description: "Buttons, input fields",
    },
    {
      name: "M",
      value: "12px",
      className: "rounded-m",
      cssVariable: "--border-radius-m",
      description: "Cards, larger components",
    },
    {
      name: "L",
      value: "16px",
      className: "rounded-l",
      cssVariable: "--border-radius-l",
      description: "Modal windows, panels",
    },
    {
      name: "Circle",
      value: "50%",
      className: "rounded-circle",
      cssVariable: "--border-radius-circle",
      description: "Circular elements",
    },
    {
      name: "Pill",
      value: "999px",
      className: "rounded-pill",
      cssVariable: "--border-radius-pill",
      description: "Pill-shaped elements",
    },
  ];

  const borderWidthExamples: BorderExample[] = [
    {
      name: "XS",
      value: "1px",
      className: "border border-xs",
      cssVariable: "--border-width-xs",
      description: "Default thin borders",
    },
    {
      name: "S",
      value: "1.5px",
      className: "border border-s",
      cssVariable: "--border-width-s",
      description: "Slightly thicker borders",
    },
    {
      name: "M",
      value: "2px",
      className: "border border-m",
      cssVariable: "--border-width-m",
      description: "Medium borders",
    },
    {
      name: "L",
      value: "4px",
      className: "border border-l",
      cssVariable: "--border-width-l",
      description: "Thick borders",
    },
    {
      name: "XL",
      value: "8px",
      className: "border border-xl",
      cssVariable: "--border-width-xl",
      description: "Extra thick borders",
    },
  ];

  const directionalBorderExamples = [
    {
      name: "Top",
      className: "border-t border-t-m",
      description: "Top border only",
    },
    {
      name: "Right",
      className: "border-r border-r-m",
      description: "Right border only",
    },
    {
      name: "Bottom",
      className: "border-b border-b-m",
      description: "Bottom border only",
    },
    {
      name: "Left",
      className: "border-l border-l-m",
      description: "Left border only",
    },
  ];

  const combinedBorderExamples = [
    {
      name: "Primary",
      className: "border-primary",
      description: "Primary border style",
    },
    {
      name: "Secondary",
      className: "border-secondary",
      description: "Secondary border style",
    },
    {
      name: "Accent",
      className: "border-accent",
      description: "Accent border style",
    },
    {
      name: "Error",
      className: "border-error",
      description: "Error state border",
    },
    {
      name: "Success",
      className: "border-success",
      description: "Success state border",
    },
  ];

  const borderStyleExamples: BorderExample[] = [
    {
      name: "Solid",
      value: "solid",
      className: "border border-solid",
      cssVariable: "--border-style-solid",
      description: "Default solid border style",
    },
    {
      name: "Dashed",
      value: "dashed",
      className: "border border-dashed",
      cssVariable: "--border-style-dashed",
      description: "Dashed border for visual separation",
    },
    {
      name: "Dotted",
      value: "dotted",
      className: "border border-dotted",
      cssVariable: "--border-style-dotted",
      description: "Dotted border for subtle emphasis",
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>Border System</div>
        </div>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Border Radius</div>
          <div className={styles.grid}>
            {borderRadiusExamples.map((example) => (
              <div key={example.name} className={styles.example}>
                <div className={styles.exampleTitle}>
                  {example.name} ({example.value})
                </div>
                <div className={styles.exampleValue}>
                  <div>.{example.className}</div>
                  <div>var({example.cssVariable})</div>
                </div>
                <div className={`${styles.demoBox} ${example.className}`} />
                <div className={styles.exampleDescription}>
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Border Width</div>
          <div className={styles.grid}>
            {borderWidthExamples.map((example) => (
              <div key={example.name} className={styles.example}>
                <div className={styles.exampleTitle}>
                  {example.name} ({example.value})
                </div>
                <div className={styles.exampleValue}>
                  <div>.{example.className}</div>
                  <div>var({example.cssVariable})</div>
                </div>
                <div className={`${styles.demoBox} ${example.className}`} />
                <div className={styles.exampleDescription}>
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Directional Borders</div>
          <div className={styles.grid}>
            {directionalBorderExamples.map((example) => (
              <div key={example.name} className={styles.example}>
                <div className={styles.exampleTitle}>{example.name}</div>
                <div className={styles.exampleValue}>.{example.className}</div>
                <div className={`${styles.demoBox} ${example.className}`} />
                <div className={styles.exampleDescription}>
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Combined Border Utilities</div>
          <div className={styles.grid}>
            {combinedBorderExamples.map((example) => (
              <div key={example.name} className={styles.example}>
                <div className={styles.exampleTitle}>{example.name}</div>
                <div className={styles.exampleValue}>.{example.className}</div>
                <div className={`${styles.demoBox} ${example.className}`} />
                <div className={styles.exampleDescription}>
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Border Style</div>
          <div className={styles.grid}>
            {borderStyleExamples.map((example) => (
              <div key={example.name} className={styles.example}>
                <div className={styles.exampleTitle}>
                  {example.name} ({example.value})
                </div>
                <div className={styles.exampleValue}>
                  <div>.{example.className}</div>
                  <div>var({example.cssVariable})</div>
                </div>
                <div
                  className={`${styles.demoBox} ${example.className} border-width-m`}
                />
                <div className={styles.exampleDescription}>
                  {example.description}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
